import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/ApiService';
import Footer from '../Footer';
import PageLoader from '../ui/PageLoader';
import Button from '../ui/Button';
import PublicHomeHero from './PublicHomeHero';
import HomeProofStrip from './HomeProofStrip';
import FeaturedBuildCard from './FeaturedBuildCard';
import HomeSection from './HomeSection';
import HowBiltWorks from './HowBiltWorks';
import HomeFeatureMosaic from './HomeFeatureMosaic';
import HomeSocialProof from './HomeSocialProof';
import HomeAudienceSplit from './HomeAudienceSplit';
import ProductUpdates from './ProductUpdates';
import SponsoredPlacement from './SponsoredPlacement';
import {
  normalizeSponsorship,
  type AdCreative,
  type HomeVehicleCard,
  type PublicHomeData,
} from './homeTypes';

function EventsProofBlock() {
  const navigate = useNavigate();
  return (
    <section className="relative min-h-[min(70vh,560px)] overflow-hidden">
      <img
        src="/images/brand/home-event-chapter-wide.jpg"
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-center"
        loading="lazy"
        decoding="async"
      />
      <div
        className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/85 to-[#0a0a0a]/25"
        aria-hidden
      />
      <div className="film-grain opacity-40 z-[1]" aria-hidden />
      <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 flex items-end min-h-[min(70vh,560px)]">
        <div className="max-w-lg">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white mb-4 text-balance">
            Every event adds another chapter
          </h2>
          <p className="text-gray-300 text-base leading-relaxed mb-8 max-w-[38ch]">
            Register once. Awards and history stay on the build, not buried in a group chat.
          </p>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              void apiService.trackEvent('home_cta_clicked', {
                metadata: { cta: 'view_events', surface: 'proof' },
              });
              navigate('/events');
            }}
          >
            View events
          </Button>
        </div>
      </div>
    </section>
  );
}

function FinalSignupCta() {
  const navigate = useNavigate();
  return (
    <section className="relative py-28 sm:py-40 overflow-hidden">
      <img
        src="/images/brand/home-story-workshop.jpg"
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
        decoding="async"
      />
      <div
        className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/90 via-[#0a0a0a]/75 to-[#0a0a0a]"
        aria-hidden
      />
      <div className="film-grain z-[1]" aria-hidden />
      <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-white mb-5 text-balance">
          Ready to tell the story?
        </h2>
        <p className="text-gray-300 text-base mb-10 max-w-md mx-auto leading-relaxed">
          Open your garage, add a build, and share one profile wherever the car shows up.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5">
          <Button
            type="button"
            variant="primary"
            size="lg"
            className="sm:px-9 shadow-brand"
            onClick={() => {
              void apiService.trackEvent('home_cta_clicked', {
                metadata: { cta: 'create_garage', surface: 'final' },
              });
              navigate('/register');
            }}
          >
            Start your garage
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            onClick={() => navigate('/discover')}
          >
            Browse builds
          </Button>
        </div>
        <p className="mt-14 font-display text-sm italic tracking-wide text-gray-500">
          The build stays with the car.
        </p>
      </div>
    </section>
  );
}

function collectHeroBuilds(data: PublicHomeData | null): HomeVehicleCard[] {
  if (!data) return [];
  const out: HomeVehicleCard[] = [];
  const seen = new Set<number>();
  const push = (v?: HomeVehicleCard | null) => {
    if (!v?.id || seen.has(v.id)) return;
    seen.add(v.id);
    out.push(v);
  };

  if (data.featured_build?.vehicle) push(data.featured_build.vehicle);
  (data.recent_builds || []).forEach(push);
  (data.recently_awarded || []).forEach(push);
  return out;
}

export const PublicHomePage: React.FC = () => {
  const [data, setData] = useState<PublicHomeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sponsorship, setSponsorship] = useState<AdCreative | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [home, ad] = await Promise.all([
          apiService.getHomePublic().catch(() => ({} as PublicHomeData)),
          apiService.getAdPlacement('home_banner').catch(() => null),
        ]);
        if (cancelled) return;
        setData(home);
        setSponsorship(normalizeSponsorship(home.sponsorship) || ad);
        void apiService.trackEvent('home_viewed', { metadata: { audience: 'public' } });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const heroBuilds = useMemo(() => collectHeroBuilds(data), [data]);
  const featured = data?.featured_build || null;

  if (loading) {
    return <PageLoader message="Loading Bilt…" />;
  }

  return (
    <div className="page">
      <PublicHomeHero
        builds={heroBuilds}
        featuredVehicleId={featured?.vehicle?.id ?? null}
      />

      <HomeProofStrip />

      <HowBiltWorks />
      <HomeFeatureMosaic />
      <HomeSocialProof testimonials={data?.testimonials} />

      {featured ? (
        <HomeSection
          title="Featured build"
          description="A build worth studying while you start yours."
          tone="muted"
        >
          <FeaturedBuildCard featured={featured} />
        </HomeSection>
      ) : null}

      {sponsorship ? <SponsoredPlacement creative={sponsorship} /> : null}

      <EventsProofBlock />
      <HomeAudienceSplit />

      {(data?.product_updates?.length ?? 0) > 0 ? (
        <ProductUpdates updates={data!.product_updates!} />
      ) : null}

      <FinalSignupCta />
      <Footer />
    </div>
  );
};

export default PublicHomePage;
